import React, { useRef, useState } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";

const testimonials = [
  {
    id: 1,
    name: "Sarah Johnson",
    role: "Business Professional",
    image: "https://randomuser.me/api/portraits/women/32.jpg",
    text: "Language Village transformed my career opportunities. After just 6 months of Spanish lessons, I secured a promotion that required bilingual skills. The instructors are patient and the curriculum is perfectly paced.",
    fullStory:
      "I had been working at my company for three years and was eager to advance, but all senior positions required Spanish fluency due to our Latin American clients. I tried several language apps but couldn't get past basic phrases. When I joined Language Village, my instructor Maria created a personalized curriculum focused on business Spanish. The live conversation practice with native speakers and industry-specific vocabulary modules were exactly what I needed. Within 6 months, I was confidently conducting client meetings in Spanish. My manager was so impressed that I was promoted to International Account Manager with a 30% salary increase. Language Village didn't just teach me a language – it transformed my career trajectory.",
    language: "Spanish",
    rating: 5,
    yearStarted: 2022,
  },
  {
    id: 2,
    name: "Michael Chen",
    role: "Software Engineer",
    image: "https://randomuser.me/api/portraits/men/54.jpg",
    text: "I tried several language apps before finding Language Village. The difference is night and day. The personalized feedback and conversation practice helped me become conversational in French in just 4 months.",
    fullStory:
      "As a software engineer, I'm used to learning new programming languages, but human languages proved much more challenging. I spent two years using various apps to learn French with minimal progress. When my company opened an office in Paris, I knew I needed a better solution. Language Village's approach was completely different – instead of memorizing phrases, my instructor Pierre focused on practical conversation from day one. The adaptive technology identified my learning patterns and adjusted lessons accordingly. The immersive virtual environments where I could practice ordering in cafés or discussing technical concepts were incredibly effective. After just 4 months, I was able to join video calls with our French team without a translator. Now, a year later, I've relocated to Paris and conduct all my work in French. The return on investment has been incredible.",
    language: "French",
    rating: 5,
    yearStarted: 2021,
  },
  {
    id: 3,
    name: "Aisha Patel",
    role: "Medical Student",
    image: "https://randomuser.me/api/portraits/women/45.jpg",
    text: "The immersive approach at Language Village made learning Arabic enjoyable rather than overwhelming. I can now communicate with patients in their native language, which has been invaluable for my medical practice.",
    fullStory:
      "During my medical residency, I noticed many Arabic-speaking patients struggling to communicate their symptoms through interpreters. This often led to delays in treatment and patient anxiety. I decided to learn Arabic but was intimidated by the completely different writing system and sounds. Language Village's medical Arabic program was exactly what I needed. My instructor Fatima broke down the language into manageable components and focused on medical terminology from the beginning. The cultural context she provided was just as valuable as the language instruction. The mobile app allowed me to practice during breaks at the hospital, and the speech recognition technology helped perfect my pronunciation. Within 8 months, I could conduct basic patient interviews in Arabic. The gratitude on patients' faces when I speak their language is incredibly rewarding. My hospital has now sponsored five other residents to take Language Village courses after seeing the positive impact on patient care.",
    language: "Arabic",
    rating: 5,
    yearStarted: 2022,
  },
  {
    id: 4,
    name: "David Müller",
    role: "Retired Teacher",
    image: "https://randomuser.me/api/portraits/men/76.jpg",
    text: "At 68, I thought learning Italian would be too difficult, but Language Village's patient instructors and senior-friendly approach proved me wrong. I'm now planning my dream trip to Italy with confidence.",
    fullStory:
      "After retiring from 40 years of teaching mathematics, I wanted to fulfill my lifelong dream of exploring Italy and connecting with my ancestral roots. I was concerned that at 68, my ability to learn a new language would be limited. Other programs moved too quickly or were designed for younger learners. Language Village paired me with Sophia, an instructor experienced in teaching older adults. She adjusted the pace to suit my learning style and incorporated my interest in Italian history and art into our lessons. The platform's larger text options and clear audio were perfect for my needs. I particularly appreciated the flexible scheduling that allowed me to learn when my energy was highest. After 10 months, I'm now comfortable with everyday Italian conversation. I recently booked a 3-month stay in Tuscany and am excited to use my language skills with locals. Language Village showed me that age is truly no barrier to learning.",
    language: "Italian",
    rating: 5,
    yearStarted: 2023,
  },
  {
    id: 5,
    name: "Yuki Tanaka",
    role: "Marketing Executive",
    image: "https://randomuser.me/api/portraits/women/65.jpg",
    text: "Language Village's German course helped me secure a dream position in Berlin. Their business-focused curriculum and cultural training made the international transition seamless.",
    fullStory:
      "When a career opportunity arose in our Berlin office, I knew my basic German wouldn't be sufficient for a leadership role. Language Village's Business German track was exactly what I needed. My instructor Klaus focused on vocabulary relevant to marketing and management, while also incorporating cultural business norms. The role-playing scenarios where we practiced negotiations, presentations, and team meetings were incredibly valuable. The flexible scheduling allowed me to maintain my demanding work schedule while preparing for the move. Within 6 months, I reached B2 proficiency and successfully interviewed for the position entirely in German. Since relocating, I've continued advanced lessons with Language Village online. My German colleagues have commented on how quickly I've integrated into the team and local culture. What sets Language Village apart is their understanding that language learning for professionals isn't just about vocabulary—it's about cultural integration and industry-specific communication.",
    language: "German",
    rating: 5,
    yearStarted: 2022,
  },
];

const TestimonialsSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTestimonial, setSelectedTestimonial] = useState(null);
  const [showAllTestimonials, setShowAllTestimonials] = useState(false);

  const openModal = (testimonial) => {
    setSelectedTestimonial(testimonial);
    setShowAllTestimonials(false);
    setModalOpen(true);
    document.body.style.overflow = "hidden";
  };

  const openAllTestimonials = () => {
    setSelectedTestimonial(null);
    setShowAllTestimonials(true);
    setModalOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeModal = () => {
    setModalOpen(false);
    document.body.style.overflow = "auto";
  };

  // Display only the first 3 testimonials in the main view
  const displayedTestimonials = testimonials.slice(0, 3);

  return (
    <section
      ref={sectionRef}
      className="py-20 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden"
      id="testimonials"
    >
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full translate-x-1/2 -translate-y-1/2"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary/5 rounded-full -translate-x-1/3 translate-y-1/3"></div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            Student <span className="text-primary">Success Stories</span>
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Hear from our students who have transformed their lives through
            language learning with our expert instructors and proven
            methodology.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {displayedTestimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.id}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              className="bg-white rounded-xl shadow-lg overflow-hidden flex flex-col h-full"
            >
              <div className="p-6 flex-grow">
                <div className="flex items-center mb-4">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-16 h-16 rounded-full object-cover mr-4 border-2 border-primary"
                  />
                  <div>
                    <h3 className="font-bold text-gray-800">
                      {testimonial.name}
                    </h3>
                    <p className="text-gray-500 text-sm">{testimonial.role}</p>
                    <div className="flex items-center mt-1">
                      <span className="text-xs font-medium text-primary mr-2">
                        {testimonial.language}
                      </span>
                      <div className="flex">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <svg
                            key={i}
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4 text-yellow-500"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                <p className="text-gray-600 italic">"{testimonial.text}"</p>
              </div>
              <div className="bg-gradient-to-r from-primary/10 to-primary/5 px-6 py-4 mt-auto">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">
                    Student since {testimonial.yearStarted}
                  </span>
                  <button
                    onClick={() => openModal(testimonial)}
                    className="text-primary font-medium text-sm hover:underline focus:outline-none"
                  >
                    Read full story →
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-12 text-center"
        >
          <button
            onClick={openAllTestimonials}
            className="bg-white hover:bg-gray-50 text-primary font-medium py-3 px-6 rounded-full border border-primary transition duration-300 shadow-sm hover:shadow"
          >
            View More Success Stories
          </button>
        </motion.div>
      </div>

      {/* Modal */}
      <AnimatePresence>
        {modalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", damping: 20 }}
              className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {selectedTestimonial ? (
                // Single testimonial view
                <div className="p-6 md:p-8">
                  <div className="flex justify-between items-start mb-6">
                    <div className="flex items-center">
                      <img
                        src={selectedTestimonial.image}
                        alt={selectedTestimonial.name}
                        className="w-20 h-20 rounded-full object-cover mr-5 border-2 border-primary"
                      />
                      <div>
                        <h3 className="text-2xl font-bold text-gray-800">
                          {selectedTestimonial.name}
                        </h3>
                        <p className="text-gray-600">
                          {selectedTestimonial.role}
                        </p>
                        <div className="flex items-center mt-2">
                          <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm mr-3">
                            {selectedTestimonial.language}
                          </span>
                          <div className="flex">
                            {[...Array(selectedTestimonial.rating)].map(
                              (_, i) => (
                                <svg
                                  key={i}
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-5 w-5 text-yellow-500"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                >
                                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                </svg>
                              )
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={closeModal}
                      className="text-gray-500 hover:text-gray-700 p-2"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-xl mb-6">
                    <h4 className="text-lg font-medium text-gray-800 mb-3">
                      Success Story
                    </h4>
                    <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                      {selectedTestimonial.fullStory}
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-4 items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mr-3">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5 text-primary"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Student since</p>
                        <p className="font-medium">
                          {selectedTestimonial.yearStarted}
                        </p>
                      </div>
                    </div>

                    <button
                      onClick={() => {
                        closeModal();
                        setTimeout(() => {
                          const element =
                            document.getElementById("contact-section");
                          if (element) {
                            element.scrollIntoView({ behavior: "smooth" });
                          }
                        }, 300);
                      }}
                      className="bg-primary hover:bg-primary-dark text-white font-medium py-2 px-6 rounded-full transition duration-300"
                    >
                      Start Your Journey
                    </button>
                  </div>
                </div>
              ) : (
                // All testimonials view
                <div className="p-6 md:p-8">
                  <div className="flex justify-between items-center mb-8">
                    <h3 className="text-2xl font-bold text-gray-800">
                      All Success Stories
                    </h3>
                    <button
                      onClick={closeModal}
                      className="text-gray-500 hover:text-gray-700 p-2"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>

                  <div className="space-y-6">
                    {testimonials.map((testimonial) => (
                      <div
                        key={testimonial.id}
                        className="border border-gray-100 rounded-xl p-5 hover:shadow-md transition-shadow duration-300"
                      >
                        <div className="flex items-center mb-4">
                          <img
                            src={testimonial.image}
                            alt={testimonial.name}
                            className="w-16 h-16 rounded-full object-cover mr-4 border-2 border-primary"
                          />
                          <div>
                            <h4 className="font-bold text-gray-800">
                              {testimonial.name}
                            </h4>
                            <p className="text-gray-500 text-sm">
                              {testimonial.role}
                            </p>
                            <div className="flex items-center mt-1">
                              <span className="text-xs font-medium text-primary mr-2">
                                {testimonial.language}
                              </span>
                              <div className="flex">
                                {[...Array(testimonial.rating)].map((_, i) => (
                                  <svg
                                    key={i}
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-4 w-4 text-yellow-500"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                  >
                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                  </svg>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                        <p className="text-gray-600 italic mb-3">
                          "{testimonial.text}"
                        </p>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-500">
                            Student since {testimonial.yearStarted}
                          </span>
                          <button
                            onClick={() => openModal(testimonial)}
                            className="text-primary font-medium text-sm hover:underline focus:outline-none"
                          >
                            Read full story →
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-8 text-center">
                    <button
                      onClick={() => {
                        closeModal();
                        setTimeout(() => {
                          const element =
                            document.getElementById("contact-section");
                          if (element) {
                            element.scrollIntoView({ behavior: "smooth" });
                          }
                        }, 300);
                      }}
                      className="bg-primary hover:bg-primary-dark text-white font-medium py-3 px-8 rounded-full transition duration-300"
                    >
                      Start Your Language Journey
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default TestimonialsSection;
